import { Check, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import type { ScenarioSummary } from "@/lib/scenarios";
import { CapBadge } from "./atoms";

type ContinueCardProps = {
  scenario: ScenarioSummary;
};

/**
 * Hero "Continue" card on the Skills home. Two visual modes — the
 * normal in-progress state and the elevated ready-for-checkpoint
 * state (gradient + ring shadow + different CTA copy).
 *
 * Mockup: `design/hifi/scenarios.jsx` lines ~275–338.
 *
 * Phase 4 keeps this read-only — clicking the CTA navigates to
 * Detail. Phase 5 wires the actual session flow.
 */
export function ContinueCard({ scenario }: ContinueCardProps) {
  const t = useT();
  const ready = scenario.progress?.status === "READY_FOR_CHECKPOINT";

  return (
    <div
      className="relative overflow-hidden rounded-[var(--r-lg)] border bg-surface p-[18px]"
      style={{
        borderColor: "var(--scen-solid)",
        borderWidth: 1.5,
        // Elevated gradient on ready_check — visually anticipates
        // the checkpoint moment without dominating the rest of the
        // home screen.
        background: ready ? "linear-gradient(180deg, var(--scen-bg) 0%, var(--surface) 60%)" : "var(--surface)",
      }}
    >
      <div className="mb-2 flex items-center gap-2">
        {ready ? (
          <CapBadge sm solid>
            <Check className="size-2.5" strokeWidth={3} />
            {t("skills.continue_ready_check")}
          </CapBadge>
        ) : (
          <CapBadge sm>{t("skills.continue_in_progress")}</CapBadge>
        )}
        <span className="font-mono text-[11px] text-text-3">
          {t("skills.detail_progress_minutes", {
            n: ready ? "5" : Math.max(scenario.estimatedMinutes - 12, 5).toString(),
          })}
        </span>
      </div>
      <h2 className="mb-1 text-[19px] font-bold tracking-tight text-text">{scenario.title}</h2>
      <p className="mb-4 text-[13px] leading-relaxed text-text-2">{scenario.promise}</p>

      <Button asChild variant="scenario" size="block">
        {/* IN_PROGRESS resumes inside the session flow.
            READY_FOR_CHECKPOINT jumps directly to the checkpoint —
            the bridge screen is only there as a soft pause; once
            status has flipped, we're past it. */}
        <Link to={ready ? `/skills/${scenario.slug}/checkpoint` : `/skills/${scenario.slug}/session`}>
          {ready ? (
            <>
              <Check strokeWidth={2.5} />
              {t("skills.submit_checkpoint")}
            </>
          ) : (
            <>
              <Play strokeWidth={2} />
              {t("skills.continue")}
            </>
          )}
        </Link>
      </Button>
    </div>
  );
}
